import { useEffect, useRef, useState } from "react";

// ===== MINI SIDEBAR (COLLAPSE) + MOBILE OPEN/CLOSE BEHAVIOR =====
// Hover-to-expand on desktop, swipe + overlay/escape close on mobile.
export const useSidebarCollapse = ({
  isMobile,
  isRTL,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const sidebarRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Deliberate delays, kept small on purpose.
  //
  // OPEN was 250ms. That is a quarter second of nothing happening before the
  // sidebar even starts to expand, and it was being added on top of the
  // render cost of the page underneath — which is what made opening feel
  // sluggish. 80ms is still enough to avoid the sidebar flying open when the
  // pointer merely crosses it on the way somewhere else, without reading as
  // lag. CLOSE stays longer than OPEN so the sidebar does not snap shut while
  // the pointer briefly leaves it.
  const OPEN_DELAY_MS = 80;
  const CLOSE_DELAY_MS = 150;

  const handleMouseEnter = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(false);
      }, OPEN_DELAY_MS);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
      }, CLOSE_DELAY_MS);
    }
  };

  const handleSidebarToggle = (event) => {
    event.stopPropagation();
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Never leave the sidebar in the collapsed state on mobile
  useEffect(() => {
    if (isMobile && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMobile, isCollapsed]);

  // ===== TOUCH SWIPE STATE =====
  const [touchStart, setTouchStart] = useState({ x: null, y: null });
  const handleTouchStart = (e) =>
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  const handleTouchMove = (e) => {
    if (touchStart.x === null || touchStart.y === null) return;
    const diffX = touchStart.x - e.touches[0].clientX;
    const diffY = touchStart.y - e.touches[0].clientY;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (isRTL) {
        if (diffX > 0 && !sidebarOpen) setSidebarOpen(true);
        if (diffX < 0 && sidebarOpen) setSidebarOpen(false);
      } else {
        if (diffX < 0 && !sidebarOpen) setSidebarOpen(true);
        if (diffX > 0 && sidebarOpen) setSidebarOpen(false);
      }
      setTouchStart({ x: null, y: null });
    }
  };
  const handleTouchEnd = () => setTouchStart({ x: null, y: null });

  // Close the mobile sidebar when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const menuButton = event.target.closest(".menu-btn");
        const collapseButton = event.target.closest(".collapse-btn");
        if (!menuButton && !collapseButton && sidebarOpen && isMobile) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen, isMobile]);

  // The hover timer could otherwise fire after unmount.
  useEffect(() => () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  return {
    isCollapsed,
    setIsCollapsed,
    hoveredNavItem,
    setHoveredNavItem,
    sidebarRef,
    handleMouseEnter,
    handleMouseLeave,
    handleSidebarToggle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
